import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';

/** Одна компенсирующая операция на сервере — один шаг отмены. */
export interface UndoOperation {
  label: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

export type UndoResult =
  | { status: 'ok'; label: string }
  | { status: 'empty' | 'busy' | 'cancelled' }
  | { status: 'failed'; label: string };

export type UndoRecorder = (operation: UndoOperation) => void;

/** Карточки кандидатов и таблица 2026 используют одну историю. */
export const CANDIDATES_UNDO_CONTEXT = 'candidates:2026';
export const sheetUndoContext = (code: string) => `sheet:${code}`;
const MAX_DEPTH = 100;

function createHistory() {
  return {
    undo: ref<UndoOperation[]>([]),
    redo: ref<UndoOperation[]>([]),
    busy: ref(false),
    revision: 0,
  };
}

const histories = new Map<string, ReturnType<typeof createHistory>>();
let generation = 0;

function historyFor(context: string) {
  let history = histories.get(context);
  if (!history) {
    history = createHistory();
    histories.set(context, history);
  }
  return history;
}

/** Истории живут до выхода из учётной записи, независимо от монтажа экранов. */
export function useUndoStack(context: MaybeRefOrGetter<string> = CANDIDATES_UNDO_CONTEXT) {
  const current = () => historyFor(toValue(context));
  const undoStack = computed(() => current().undo.value);
  const redoStack = computed(() => current().redo.value);
  const busy = computed(() => current().busy.value);

  /**
   * Вызывается ДО первого await сохранения. Поздний ответ записывается в
   * исходный лист, а после выхода из учётной записи больше не записывается.
   */
  function capture(): UndoRecorder {
    const history = current();
    const epoch = generation;
    return (operation) => {
      if (epoch !== generation) return;
      history.revision++;
      history.undo.value.push(operation);
      if (history.undo.value.length > MAX_DEPTH) history.undo.value.shift();
      history.redo.value = [];
    };
  }

  function record(operation: UndoOperation): void {
    capture()(operation);
  }

  /** Выход и смена пользователя очищают ВСЕ листы, включая фоновые операции. */
  function clear(): void {
    generation++;
    for (const history of histories.values()) {
      history.revision++;
      history.undo.value = [];
      history.redo.value = [];
      history.busy.value = false;
    }
  }

  async function run(
    direction: 'undo' | 'redo',
    history = current(),
    epoch = generation,
  ): Promise<UndoResult> {
    // Контекст фиксируется до await: переход на другой лист не меняет
    // адрес завершения, ветку повтора и индикатор занятости этой операции.
    if (epoch !== generation) return { status: 'cancelled' };
    if (history.busy.value) return { status: 'busy' };
    const operation = history[direction].value.pop();
    if (!operation) return { status: 'empty' };
    const branch = history.revision;
    const position = history.undo.value.length;
    history.busy.value = true;

    try {
      await operation[direction]();
      if (epoch !== generation) return { status: 'cancelled' };
      if (direction === 'undo') {
        if (branch === history.revision) history.redo.value.push(operation);
      } else {
        history.undo.value.splice(position, 0, operation);
      }
      return { status: 'ok', label: operation.label };
    } catch {
      if (epoch !== generation) return { status: 'cancelled' };
      if (direction === 'undo') history.undo.value.splice(position, 0, operation);
      else if (branch === history.revision) history.redo.value.push(operation);
      return { status: 'failed', label: operation.label };
    } finally {
      if (epoch === generation) history.busy.value = false;
    }
  }

  /** Команда может ждать очередь сохранений, но не переходить в новую сессию. */
  function captureCommand(direction: 'undo' | 'redo'): () => Promise<UndoResult> {
    const history = current();
    const epoch = generation;
    return () => run(direction, history, epoch);
  }

  return {
    undoStack,
    redoStack,
    busy,
    record,
    capture,
    captureCommand,
    clear,
    undo: () => run('undo'),
    redo: () => run('redo'),
  };
}
